<!--
  ClientsListPage.vue -- Distributor Clients list (TABLE surface).

  Card: 2026-06-13 design bundle implementation -- https://trello.com/c/hYlwyKYT
  Pebble: distributor client-list table + soft-delete.

  History: this surface began as an attention-first CARD workspace (a
  <ul> of ClientsListRow cards). Jake's decision (2026-07-13) REPLACES
  that card design with a TABLE (Name | Contact | Email | Phone | Stores | Status
  | Actions) carrying a per-row deactivate (soft-delete) action. This
  also corrects PR #2037, which mistakenly converted the DEAD legacy
  ClientList.vue instead of this live component -- the route /clients
  (name client_list) mounts ClientsListPage.vue.

  The attention-first filter chips (Issues / Onboarding / Active /
  Quiet / All), the name/contact/store search, and the Invite CTA are
  preserved from the workspace design; only the list presentation
  changes from cards to table rows. The status filter does NOT hard-hide
  inactive clients: the "All" chip (default) surfaces them, so a
  deactivated client stays findable.

  Deactivate (soft-delete): a per-row action gated on ``client.update``
  opens a confirm modal (copy = "deactivate"/"make inactive", never
  "delete") -> PATCH status='inactive' through the capability-gated,
  tenant-scoped SAFRS Client surface (verified in #2037). The row STAYS
  in the table, flips to Inactive (de-emphasized), and persists across
  reload. Mirrors the SA ClientsList.vue soft-delete affordance so the
  two surfaces read as one design system.

  Selector convention: data-testid on every interactive element.
-->
<template>
    <v-container fluid class="main-container">
        <div class="cw-page" data-testid="clients-workspace-page">
            <header class="cw-page__header">
                <div class="d-flex align-center">
                    <h2 class="cw-page__title">Clients</h2>
                    <HelpIcon
                        audience="owner"
                        slug="manage-clients"
                        heading="read-the-list"
                    />
                </div>
                <!--
                  "Invite a Client" creates a brand-new Client row, which
                  is inherently distributor-scoped (clients.distributor_id
                  is NOT NULL). A Super Admin who is NOT impersonating
                  has no tenant to scope the new Client to -- the backend
                  rejects POST /clients/ with a clean 400 ("Cannot create
                  a client without a distributor context."). Hide the
                  affordance rather than let the user fill out a form
                  that will fail on submit. Mirrors the gate pattern in
                  StoreClientContact.vue (PR-R, #1679). Audit F2,
                  2026-06-24.
                -->
                <div class="cw-page__actions" v-if="canInviteEffective">
                    <button
                        type="button"
                        class="cw-btn cw-btn--primary"
                        data-testid="clients-workspace-invite-btn"
                        @click="openInviteForNewClient"
                    >
                        Invite a Client
                    </button>
                </div>
                <p
                    v-else-if="canInvite && !hasDistributorContext"
                    class="cw-invite-gated-note"
                    data-testid="clients-workspace-invite-gated-note"
                >
                    Impersonate a distributor to invite a Client.
                </p>
            </header>

            <div class="cw-toolbar">
                <ClientsFilterChips
                    :filter="store.filter"
                    :counts="filterCounts"
                    data-testid="clients-workspace-filter-chips"
                    @change="onFilterChange"
                />
                <input
                    v-model="searchInput"
                    type="search"
                    class="cw-search"
                    placeholder="Search clients, contacts, or stores"
                    data-testid="clients-workspace-search"
                    @input="onSearchInput"
                />
            </div>

            <div
                v-if="banner"
                class="cw-banner"
                data-testid="clients-workspace-banner"
                role="status"
            >
                {{ banner }}
            </div>

            <div
                v-if="store.loading"
                class="cw-loading"
                data-testid="clients-workspace-loading"
            >
                <v-progress-circular indeterminate color="primary" />
                <span class="cw-loading-text">Loading clients...</span>
            </div>

            <div
                v-else-if="store.error"
                class="cw-error"
                data-testid="clients-workspace-error"
            >
                {{ store.error }}
            </div>

            <ClientsEmptyState
                v-else-if="totalCount === 0"
                :can-invite="canInvite"
                data-testid="clients-workspace-empty"
                @invite="openInviteForNewClient"
            />

            <div
                v-else-if="visibleClients.length === 0"
                class="cw-no-matches"
                data-testid="clients-workspace-no-matches"
            >
                No clients match the current filter. Try a different
                chip or clear the search.
            </div>

            <table v-else class="cw-table" data-testid="clients-table">
                <thead>
                    <tr>
                        <th class="cw-table__header">Client company</th>
                        <th class="cw-table__header" data-testid="clients-contact-header">Contact</th>
                        <th class="cw-table__header">Email</th>
                        <th class="cw-table__header">Phone</th>
                        <th class="cw-table__header" data-testid="clients-people-header">People</th>
                        <th class="cw-table__header">Stores</th>
                        <th class="cw-table__header">Status</th>
                        <th
                            class="cw-table__header cw-table__header--actions"
                        >
                            Actions
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr
                        v-for="row in visibleClients"
                        :key="row.id"
                        :data-testid="`clients-row-${row.id}`"
                        class="cw-table__row cw-table__row--clickable"
                        :class="{
                            'cw-table__row--inactive':
                                row.status === 'inactive',
                        }"
                        @click="openClient(row)"
                    >
                        <td class="cw-cell cw-cell--name">
                            {{ row.name || "--" }}
                        </td>
                        <td
                            class="cw-cell cw-cell--contact"
                            :data-testid="`clients-contact-${row.id}`"
                        >
                            {{ row.contact_name || "--" }}
                        </td>
                        <td class="cw-cell cw-cell--email">
                            {{ row.email || "--" }}
                        </td>
                        <td class="cw-cell cw-cell--phone">
                            {{ row.phone || "--" }}
                        </td>
                        <td
                            class="cw-cell cw-cell--people"
                            :data-testid="`clients-people-count-${row.id}`"
                        >
                            {{ peopleCopy(row) }}
                        </td>
                        <td class="cw-cell cw-cell--stores">
                            {{ storeCopy(row) }}
                        </td>
                        <td class="cw-cell cw-cell--status">
                            <span
                                class="cw-status-chip"
                                :class="`cw-status-chip--${
                                    row.status || 'active'
                                }`"
                                :data-testid="`clients-status-chip-${row.id}`"
                            >
                                {{ clientStatusLabel(row.status) }}
                            </span>
                        </td>
                        <td class="cw-cell cw-cell--actions">
                            <!-- "View as client": impersonate this OWN client
                                 into the Client Portal (card
                                 https://trello.com/c/k8m2iLzG). Gated on
                                 distributor.impersonate_customer (Owner /
                                 Builder / Sales Executive). Hidden for inactive
                                 clients (cannot view as a soft-deleted client).
                                 DISABLED (with a tooltip) when the client is not
                                 impersonatable -- it has no active portal
                                 account, so entering the portal would 403.
                                 The backend re-checks ownership (404) + the 422
                                 gates as a safety net for stale rollup data. -->
                            <button
                                v-if="canViewAsCustomer && row.status !== 'inactive'"
                                type="button"
                                class="cw-btn"
                                :data-testid="`clients-view-as-customer-btn-${row.id}`"
                                :disabled="viewingAsId === row.id || !row.impersonatable"
                                :title="row.impersonatable ? '' : 'This client has no active portal account yet'"
                                @click.stop="doViewAsCustomer(row)"
                            >
                                View as client
                            </button>
                            <!-- The lifecycle toggle: an active client can
                                 be deactivated (soft-delete, via confirm
                                 modal); an inactive one can be reactivated
                                 (low-risk restore, no modal). Both PATCH
                                 the client.update-gated SAFRS surface. -->
                            <button
                                v-if="canDeactivate && row.status === 'inactive'"
                                type="button"
                                class="cw-btn"
                                :data-testid="`clients-reactivate-btn-${row.id}`"
                                :disabled="reactivatingId === row.id"
                                @click.stop="doReactivate(row)"
                            >
                                Reactivate
                            </button>
                            <button
                                v-else-if="canDeactivate"
                                type="button"
                                class="cw-btn cw-btn--danger"
                                :data-testid="`clients-deactivate-btn-${row.id}`"
                                @click.stop="confirmDeactivate(row)"
                            >
                                Deactivate
                            </button>
                        </td>
                    </tr>
                </tbody>
            </table>

            <!-- Deactivate confirm modal (inline; small surface).
                 Soft-delete: the client is marked INACTIVE rather than
                 removed, so its stores and history survive and it can be
                 reactivated. Copy deliberately avoids "delete". -->
            <div
                v-if="pendingDeactivate"
                class="cw-modal-backdrop"
                data-testid="clients-deactivate-modal"
            >
                <div class="cw-modal">
                    <h3>Deactivate client?</h3>
                    <p>
                        Make
                        <strong>{{ pendingDeactivate.name }}</strong>
                        inactive? The client stays in the list
                        (de-emphasized) and its stores and history are
                        kept. This is a deactivate, not removal -- you
                        can reactivate it later.
                    </p>
                    <div
                        v-if="deactivateError"
                        class="cw-modal__error"
                        data-testid="clients-deactivate-error"
                    >
                        {{ deactivateError }}
                    </div>
                    <div class="cw-modal__actions">
                        <button
                            type="button"
                            class="cw-btn"
                            data-testid="clients-deactivate-cancel"
                            @click="cancelDeactivate"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            class="cw-btn cw-btn--danger"
                            data-testid="clients-deactivate-confirm"
                            :disabled="deactivateBusy"
                            @click="doDeactivate"
                        >
                            Deactivate
                        </button>
                    </div>
                </div>
            </div>

            <!--
              Invite modal reused from F5 (ADR 0050). "Invite a Client"
              from the top bar opens it in CREATE-NEW mode: it mints a
              brand-new Client (tenant-scoped/impersonation-aware) then
              sends the first invitation.
            -->
            <InviteClientModal
                :open="inviteOpen"
                :client-id="inviteClientId"
                :client-name="inviteClientName"
                :create-new="true"
                @close="closeInvite"
                @sent="onInvitationSent"
            />
        </div>
    </v-container>
</template>

<script>
import { useClientsWorkspaceStore } from '@/stores/clientsWorkspace'
import { useImpersonationStore } from '@/stores/impersonation'
import ClientsFilterChips from './ClientsFilterChips.vue'
import ClientsEmptyState from './ClientsEmptyState.vue'
import InviteClientModal from './InviteClientModal.vue'
import HelpIcon from '@/components/Help/HelpIcon.vue'

// Readable copy for each client lifecycle status. The soft-delete
// affordance moves a client to ``inactive``; ``lead`` / ``active`` are
// the other two states enforced by the Client.status validator. An
// unknown code renders title-cased rather than silently blank -- a loud
// fallback that flags a new backend code during dev. Mirrors the SA
// ClientsList.vue label map so the two surfaces stay coherent.
const CLIENT_STATUS_LABELS = {
    lead: 'Lead',
    active: 'Active',
    inactive: 'Inactive',
}

function clientStatusLabel(code) {
    if (!code) return CLIENT_STATUS_LABELS.active
    if (CLIENT_STATUS_LABELS[code]) return CLIENT_STATUS_LABELS[code]
    return String(code)
        .replace(/_/g, ' ')
        .replace(/^\w/, (ch) => ch.toUpperCase())
}

export default {
    name: 'ClientsListPage',
    components: {
        HelpIcon,
        ClientsFilterChips,
        ClientsEmptyState,
        InviteClientModal,
    },
    inject: {
        distributorLayoutCapabilities: { default: () => [] },
    },
    data() {
        return {
            searchInput: '',
            // Invite modal state. clientId === 0 indicates the "invite a
            // brand-new client" path (the F5 modal accepts any client_id
            // but the workspace's intent is a fresh invitation -- the
            // backend mints a new Client row server-side via the modal
            // flow; until that exists in F5, the button is hidden when
            // there are no Clients yet). In practice the button opens
            // for the first existing client so the Distributor can
            // re-use the address book; the empty-state CTA takes the
            // "no Clients" case via its own path.
            inviteOpen: false,
            inviteClientId: 0,
            inviteClientName: '',
            banner: '',
            bannerTimer: null,
            // Soft-delete (deactivate) confirm state.
            pendingDeactivate: null,
            deactivateBusy: false,
            deactivateError: '',
            // Id of the row currently being reactivated (disables its
            // button to prevent a double-click double-PATCH).
            reactivatingId: null,
            // Id of the row whose "View as client" start is in flight
            // (disables its button to prevent a double-start).
            viewingAsId: null,
        }
    },
    computed: {
        store() {
            return useClientsWorkspaceStore()
        },
        capabilities() {
            const inj = this.distributorLayoutCapabilities
            if (Array.isArray(inj)) return inj
            if (inj && typeof inj === 'object' && Array.isArray(inj.value)) {
                return inj.value
            }
            return []
        },
        canInvite() {
            return this.capabilities.includes('client.invite_admin')
        },
        // Deactivate is a write to the Client surface -- gate it on the
        // same ``client.update`` capability the backend enforces on the
        // PATCH (SA + Owner). Matches the edit/delete gate documented on
        // the /clients routes in router/index.js.
        canDeactivate() {
            return this.capabilities.includes('client.update')
        },
        // "View as client" gate: caller holds
        // distributor.impersonate_customer (Owner / Builder / Sales
        // Executive). The backend re-enforces ownership (404 on a
        // cross-tenant client) and the portal-capable/inactive gates (422),
        // so this cap check is UI convenience, not the security boundary.
        canViewAsCustomer() {
            return this.capabilities.includes(
                'distributor.impersonate_customer',
            )
        },
        impersonationStore() {
            return useImpersonationStore()
        },
        // True for any non-SA principal (Owner / Builder always have a
        // tenant), or for an SA who IS impersonating a distributor.
        // Mirrors the SaImpersonationBanner predicate and the
        // StoreClientContact.vue "Add new client" gate (PR-R, #1679).
        isSuperAdmin() {
            return Boolean(this.$store && this.$store.getters.isSuperAdmin)
        },
        hasDistributorContext() {
            return !this.isSuperAdmin || this.impersonationStore.isActive
        },
        // Effective gate for the "Invite a Client" CTA: caller must hold
        // the capability AND have a distributor context to scope the new
        // Client to.
        canInviteEffective() {
            return this.canInvite && this.hasDistributorContext
        },
        totalCount() {
            return this.store.clients.length
        },
        visibleClients() {
            return this.store.visibleClients
        },
        filterCounts() {
            // Pre-compute per-chip counts so the chip labels render
            // "Issues (3)" instead of forcing the Distributor to click
            // to discover. This is read directly from the store getter
            // semantics so the counts can't drift from the visible set.
            const counts = {
                issues: 0,
                onboarding: 0,
                active: 0,
                inactive: 0,
                quiet: 0,
                all: this.store.clients.length,
            }
            for (const c of this.store.clients) {
                for (const chip of [
                    'issues',
                    'onboarding',
                    'active',
                    'inactive',
                    'quiet',
                ]) {
                    if (this.store.matchesFilter(c, chip)) counts[chip] += 1
                }
            }
            return counts
        },
    },
    mounted() {
        this.store.fetchClients()
        this.searchInput = this.store.search
    },
    beforeUnmount() {
        if (this.bannerTimer) clearTimeout(this.bannerTimer)
    },
    methods: {
        clientStatusLabel,
        // "3 people" / "1 person"; "--" for a payload without the count.
        peopleCopy(row) {
            const n = row.people_count
            if (n === null || n === undefined) return '--'
            return `${n} ${n === 1 ? 'person' : 'people'}`
        },
        storeCopy(row) {
            const total = Number(row.store_count || 0)
            const live = Number(row.live_store_count || 0)
            if (total === 0) return 'No stores'
            if (total === 1 && live === 1) return '1 store, live'
            if (total === 1 && live === 0) return '1 store, draft'
            return `${total} stores, ${live} live`
        },
        onFilterChange(chip) {
            this.store.applyFilter(chip)
        },
        onSearchInput() {
            this.store.applySearch(this.searchInput)
        },
        openClient(row) {
            this.$router.push(`/distributor/clients/${row.id}`)
        },
        // "View as client": start an ownership-scoped client impersonation
        // and navigate to the Client Portal. On success the impersonation
        // store flips to client mode with an exit target of
        // /distributor/clients (so the banner "Stop" returns here, not the SA
        // console). A failed start (backend 404 cross-tenant / 422 gates)
        // leaves the store untouched -- no phantom banner -- and surfaces a
        // transient banner message. Card https://trello.com/c/k8m2iLzG.
        async doViewAsCustomer(row) {
            if (!row || this.viewingAsId === row.id) return
            this.viewingAsId = row.id
            try {
                await this.impersonationStore.startViewAsCustomer(
                    row.id,
                    row.name,
                )
                if (typeof window !== 'undefined' && window.location) {
                    window.location.assign('/portal')
                }
            } catch (err) {
                this.flashBanner(
                    (err &&
                        err.response &&
                        err.response.data &&
                        err.response.data.message) ||
                        (err && err.message) ||
                        'Could not view as this client.',
                )
            } finally {
                this.viewingAsId = null
            }
        },
        confirmDeactivate(row) {
            this.deactivateError = ''
            this.pendingDeactivate = row
        },
        cancelDeactivate() {
            this.pendingDeactivate = null
            this.deactivateError = ''
        },
        async doDeactivate() {
            const row = this.pendingDeactivate
            if (!row) return
            this.deactivateBusy = true
            this.deactivateError = ''
            try {
                // Soft-delete: PATCH status -> inactive through the
                // capability-gated, tenant-scoped SAFRS Client surface.
                // The store flips the local row to inactive in place so
                // it stays visible (de-emphasized) without a refetch.
                await this.store.deactivateClient(row.id)
                this.pendingDeactivate = null
                this.flashBanner(`${row.name || 'Client'} deactivated.`)
            } catch (err) {
                this.deactivateError =
                    (err && err.message) || 'Failed to deactivate client.'
            } finally {
                this.deactivateBusy = false
            }
        },
        async doReactivate(row) {
            if (!row || this.reactivatingId === row.id) return
            this.reactivatingId = row.id
            try {
                // Reactivate is low-risk (restores an inactive client to
                // active), so it skips the confirm modal -- one click ->
                // PATCH status='active' through the same client.update-
                // gated SAFRS surface. The store flips the local row in
                // place so it returns to Active without a refetch.
                await this.store.reactivateClient(row.id)
                this.flashBanner(`${row.name || 'Client'} reactivated.`)
            } catch (err) {
                this.flashBanner(
                    (err && err.message) || 'Failed to reactivate client.',
                )
            } finally {
                this.reactivatingId = null
            }
        },
        openInviteForNewClient() {
            // Defensive: the CTA is hidden without a distributor context,
            // but never open the modal if invoked anyway (e.g. via
            // programmatic call from a test or future caller) -- create
            // would 400 on submit. Audit F2, 2026-06-24.
            if (!this.hasDistributorContext) return
            // "Invite a Client" opens the modal in CREATE-NEW mode
            // (clientId 0): the modal collects a Client name + contact
            // email, CREATES the new Client (tenant-scoped to the
            // caller's effective distributor -- impersonation-aware), and
            // THEN sends the first invitation.
            //
            // Regression fixed: this used to grab ``clients[0]`` and open
            // the per-client invite modal pointed at that EXISTING client,
            // so a brand-new contact the user typed was sent as an
            // invitation against the wrong (first) client and no new
            // Client row ever persisted. Most visible while a Super Admin
            // impersonated a distributor and tried to add a client.
            this.inviteClientId = 0
            this.inviteClientName = ''
            this.inviteOpen = true
        },
        closeInvite() {
            this.inviteOpen = false
        },
        onInvitationSent(payload) {
            this.flashBanner('Invitation sent.')
            // ``refreshClient`` refetches the whole /clients/summary list,
            // so a brand-new Client created in create-new mode (which is
            // not yet in the local cache) shows up as invited. The id arg
            // is informational only.
            const id =
                (payload && payload.client_id) || this.inviteClientId
            this.store.refreshClient(id)
        },
        flashBanner(text) {
            this.banner = text
            if (this.bannerTimer) clearTimeout(this.bannerTimer)
            this.bannerTimer = setTimeout(() => {
                this.banner = ''
            }, 4000)
        },
    },
}
</script>

<style scoped>
.cw-page {
    padding: 16px;
}
.cw-page__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
    gap: 12px;
}
.cw-page__title {
    font-size: 1.25rem;
    font-weight: 600;
    margin: 0;
    color: var(--tf-fg-1);
}
.cw-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 16px;
    flex-wrap: wrap;
}
.cw-search {
    padding: 6px 10px;
    border: 1px solid var(--tf-border);
    border-radius: var(--tf-radius-xs, 4px);
    background: var(--tf-bg);
    color: var(--tf-fg-1);
    font-size: 13px;
    min-width: 280px;
}
.cw-btn {
    padding: 6px 12px;
    border: 1px solid var(--tf-border-strong);
    border-radius: var(--tf-radius-xs, 4px);
    background: var(--tf-bg);
    color: var(--tf-fg-1);
    font-size: 13px;
    cursor: pointer;
}
.cw-btn--primary {
    background: var(--tf-accent, #2c5fff);
    border-color: var(--tf-accent, #2c5fff);
    color: #fff;
}
.cw-btn--primary:hover {
    filter: brightness(0.95);
}
.cw-btn--danger {
    color: #c0392b;
    border-color: #e2b6b0;
}
.cw-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}
.cw-invite-gated-note {
    font-size: 13px;
    color: var(--tf-fg-3);
    margin: 0;
    max-width: 280px;
    text-align: right;
}
.cw-banner {
    padding: 8px 12px;
    margin-bottom: 12px;
    background: #e6f7e6;
    border: 1px solid #6cbf6c;
    color: #14571c;
    border-radius: var(--tf-radius-xs, 4px);
    font-size: 13px;
}
.cw-loading,
.cw-error,
.cw-no-matches {
    padding: 24px;
    text-align: center;
    background: var(--tf-bg);
    border: 1px dashed var(--tf-border);
    border-radius: var(--tf-radius-xs, 4px);
    color: var(--tf-fg-2);
}
.cw-loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
}
.cw-error {
    color: #c0392b;
    border-color: #c0392b;
}
/* Clients table -- a plain semantic <table> (stays testable without a
   configured Vuetify instance in vitest), tokenized to the page's
   design variables with neutral fallbacks. Mirrors the SA
   ClientsList.vue .sa-table styling so the two surfaces read as one. */
.cw-table {
    width: 100%;
    border-collapse: collapse;
    background: var(--tf-bg, #fff);
    border: 1px solid var(--tf-border, #e2e8f0);
    border-radius: var(--tf-radius-xs, 6px);
    overflow: hidden;
    margin-bottom: 16px;
}
.cw-table__header {
    text-align: left;
    background: var(--tf-surface-alt, #f7fafc);
    color: var(--tf-fg-1, #2d3748);
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    padding: 10px 12px;
    border-bottom: 1px solid var(--tf-border, #e2e8f0);
}
.cw-table__header--actions {
    text-align: right;
}
.cw-table__row td {
    padding: 10px 12px;
    border-bottom: 1px solid var(--tf-border, #edf2f7);
    font-size: 14px;
    color: var(--tf-fg-1, #1a202c);
}
.cw-table__row--clickable {
    cursor: pointer;
}
.cw-table__row--clickable:hover td {
    background: var(--tf-surface-alt, #f7fafc);
}
.cw-table__row:last-child td {
    border-bottom: none;
}
/* Soft-deleted (inactive) rows stay visible but de-emphasized. */
.cw-table__row--inactive td {
    color: var(--tf-fg-3, #94a3b8);
    background: var(--tf-surface-alt, #f8fafc);
}
.cw-cell--people {
    white-space: nowrap;
}
.cw-cell--actions {
    text-align: right;
    white-space: nowrap;
}
/* Client lifecycle status chip -- mirrors the SA list chips so the two
   surfaces read as one design system. */
.cw-status-chip {
    display: inline-block;
    padding: 2px 8px;
    font-size: 12px;
    border-radius: 12px;
    border: 1px solid var(--tf-border, #e2e8f0);
    background: var(--tf-bg, #fff);
}
.cw-status-chip--lead {
    background: #fff4e0;
    border-color: #f0b96b;
    color: #6b4500;
}
.cw-status-chip--active {
    background: #e6f7e6;
    border-color: #6cbf6c;
    color: #14571c;
}
.cw-status-chip--inactive {
    background: var(--tf-surface-alt, #f1f5f9);
    color: var(--tf-fg-3, #64748b);
}
.cw-modal-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.4);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
}
.cw-modal {
    background: var(--tf-bg, #fff);
    padding: 24px;
    border-radius: var(--tf-radius-sm, 6px);
    max-width: 420px;
    width: 90%;
    color: var(--tf-fg-1, #1a202c);
}
.cw-modal h3 {
    margin: 0 0 12px;
}
.cw-modal__error {
    margin-top: 12px;
    color: #c0392b;
    font-size: 13px;
}
.cw-modal__actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 16px;
}
.cw-modal .cw-btn--danger {
    color: #fff;
    background: #c0392b;
    border-color: #c0392b;
}
</style>
